import React from "react";
import styled from "styled-components";

function formatAmount(
  value,
  currency = "EUR",
) {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "—";
  }

  try {
    return new Intl.NumberFormat(
      "fr-FR",
      {
        style: "currency",
        currency:
          typeof currency === "string"
          && currency.trim()
            ? currency.trim().toUpperCase()
            : "EUR",
      },
    ).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${
      typeof currency === "string"
      && currency.trim()
        ? currency.trim().toUpperCase()
        : "EUR"
    }`;
  }
}

function formatDate(value) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    return "Non définie";
  }

  const normalized = value.trim();
  const date = new Date(
    `${normalized}T00:00:00`,
  );

  if (Number.isNaN(date.getTime())) {
    return normalized;
  }

  return new Intl.DateTimeFormat(
    "fr-FR",
  ).format(date);
}

function validityLabel(contract) {
  const validFrom = formatDate(
    contract.valid_from,
  );
  const validUntil = formatDate(
    contract.valid_until,
  );

  if (
    !contract.valid_from
    && !contract.valid_until
  ) {
    return "Sans limite de validité";
  }

  if (!contract.valid_from) {
    return `Jusqu’au ${validUntil}`;
  }

  if (!contract.valid_until) {
    return `À partir du ${validFrom}`;
  }

  return `Du ${validFrom} au ${validUntil}`;
}

export default function SupplierContractsList({
  contracts = [],
  selectedContractUuid = "",
  onSelectContract,
}) {
  return (
    <List
      aria-label="Contrats fournisseur"
    >
      {contracts.map((contract) => {
        const selected =
          contract.uuid === selectedContractUuid;

        return (
          <ContractItem
            key={contract.uuid}
          >
            <ContractButton
              type="button"
              onClick={() => {
                onSelectContract?.(contract);
              }}
              aria-pressed={selected}
              $selected={selected}
            >
              <ContractHeader>
                <ContractIdentity>
                  <strong>
                    {contract.contract_name}
                  </strong>

                  <ContractBadges>
                    {contract.is_default && (
                      <DefaultBadge>
                        Contrat par défaut
                      </DefaultBadge>
                    )}

                    <StatusBadge
                      $active={contract.active}
                    >
                      {contract.active
                        ? "Actif"
                        : "Inactif"}
                    </StatusBadge>
                  </ContractBadges>
                </ContractIdentity>

                <SelectionLabel>
                  {selected
                    ? "Sélectionné"
                    : "Ouvrir"}
                </SelectionLabel>
              </ContractHeader>

              <ContractDetails>
                <Detail>
                  <span>
                    Commande minimum HT
                  </span>
                  <strong>
                    {formatAmount(
                      contract.minimum_order_ht,
                      contract.currency,
                    )}
                  </strong>
                </Detail>

                <Detail>
                  <span>
                    Franco HT
                  </span>
                  <strong>
                    {formatAmount(
                      contract.franco_threshold_ht,
                      contract.currency,
                    )}
                  </strong>
                </Detail>

                <Detail>
                  <span>
                    Validité
                  </span>
                  <strong>
                    {validityLabel(contract)}
                  </strong>
                </Detail>
              </ContractDetails>
            </ContractButton>
          </ContractItem>
        );
      })}
    </List>
  );
}

const List = styled.ul`
  display: grid;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ContractItem = styled.li`
  min-width: 0;
`;

const ContractButton = styled.button`
  width: 100%;
  padding: 1rem;
  border: 1px solid
    ${({ $selected }) => (
      $selected
        ? "rgba(137, 179, 255, 0.72)"
        : "rgba(255, 255, 255, 0.1)"
    )};
  border-radius: 14px;
  background:
    ${({ $selected }) => (
      $selected
        ? "rgba(89, 132, 214, 0.18)"
        : "rgba(255, 255, 255, 0.035)"
    )};
  color: #f7f7f7;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease,
    transform 120ms ease;

  &:hover {
    border-color: rgba(137, 179, 255, 0.52);
    background: rgba(89, 132, 214, 0.12);
  }

  &:focus-visible {
    outline: 3px solid rgba(137, 179, 255, 0.42);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ContractHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const ContractIdentity = styled.div`
  display: grid;
  gap: 0.55rem;
  min-width: 0;

  > strong {
    overflow-wrap: anywhere;
    font-size: 1rem;
  }
`;

const ContractBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background:
    ${({ $active }) => (
      $active
        ? "rgba(86, 203, 142, 0.15)"
        : "rgba(255, 255, 255, 0.08)"
    )};
  color:
    ${({ $active }) => (
      $active
        ? "#9ce6bd"
        : "rgba(247, 247, 247, 0.65)"
    )};
  font-size: 0.75rem;
  font-weight: 750;
`;

const DefaultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: rgba(244, 193, 92, 0.14);
  color: #f4d58a;
  font-size: 0.75rem;
  font-weight: 750;
`;

const SelectionLabel = styled.span`
  flex: 0 0 auto;
  color: rgba(179, 207, 255, 0.9);
  font-size: 0.78rem;
  font-weight: 750;
`;

const ContractDetails = styled.dl`
  display: grid;
  grid-template-columns:
    repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0 0;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Detail = styled.div`
  display: grid;
  gap: 0.3rem;
  min-width: 0;
  padding-top: 0.75rem;
  border-top: 1px solid
    rgba(255, 255, 255, 0.07);

  span {
    color: rgba(247, 247, 247, 0.58);
    font-size: 0.75rem;
  }

  strong {
    overflow-wrap: anywhere;
    color: rgba(247, 247, 247, 0.92);
    font-size: 0.86rem;
  }
`;
